/**
 * Rota `/tasks` — Fase 2 PR-E do TD26 roadmap.
 *
 * Placeholder rico — tela em desenvolvimento. Tasks (ações em massa,
 * agendamento, dispatchers) ainda não tem backend nem fluxo definido,
 * mas o slot existe na sidebar e merece uma landing decente em vez
 * de 404.
 *
 * Em PR sucessora (Fase 3+), trocar este componente pela tela real
 * sem mudar rota — sidebar/cmdk seguem apontando aqui.
 */

import { createFileRoute } from '@tanstack/react-router';

export const Route = createFileRoute('/_auth/tasks')({
  component: TasksPage,
});

type ComingFeature = {
  title: string;
  description: string;
  icon: React.ReactNode;
};

const COMING: ComingFeature[] = [
  {
    title: 'Ações em massa',
    description:
      'Selecione N ONUs (no Cockpit via lasso/polygon TD27-09 ou na busca ⌘K) e execute em batch: resync, notificação, dispatch técnico.',
    icon: <IconBatch />,
  },
  {
    title: 'Agendamento',
    description:
      'Programe ações pra horários específicos (ex: resync OLT às 03h, comunicação Voalle no horário comercial). Recorrência simples.',
    icon: <IconCalendar />,
  },
  {
    title: 'Dispatcher integrado',
    description:
      'Criar OS no Voalle direto da seleção do mapa, com técnico sugerido por proximidade. Status sincroniza com Tasks.',
    icon: <IconRoute />,
  },
  {
    title: 'Histórico + auditoria',
    description:
      'Toda task executada fica registrada com user, payload, resultado. Filtrável por OLT, técnico, status, período.',
    icon: <IconHistory />,
  },
];

function TasksPage() {
  return (
    <div className="flex h-full w-full flex-col overflow-y-auto">
      {/* Hero */}
      <div className="border-b border-border bg-bg-elevated p-6">
        <div className="flex items-start gap-4">
          <div className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-accent/15 text-accent">
            <IconTasks />
          </div>
          <div>
            <div className="flex items-baseline gap-3">
              <h1 className="text-xl font-semibold text-fg">Tasks</h1>
              <span className="rounded-md border border-status-warning/40 bg-status-warning/10 px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide text-status-warning">
                em desenvolvimento
              </span>
            </div>
            <p className="mt-1 max-w-2xl text-sm text-fg-subtle">
              Centro de ações em massa, agendamentos e dispatch. Substitui o vai-e-vem entre
              cockpit, Voalle e WhatsApp pelo operador atender N clientes de uma vez.
            </p>
          </div>
        </div>
      </div>

      {/* Coming features */}
      <div className="p-6">
        <h2 className="mb-4 text-xs font-medium uppercase tracking-wide text-fg-subtle">
          Em construção
        </h2>
        <div className="grid gap-4 [grid-template-columns:repeat(auto-fill,minmax(320px,1fr))]">
          {COMING.map((f) => (
            <article
              key={f.title}
              className="flex flex-col gap-3 rounded-lg border border-border bg-bg-elevated p-4"
            >
              <div className="flex items-center gap-2">
                <span className="grid h-8 w-8 place-items-center rounded-md bg-bg-inset text-fg-muted">
                  {f.icon}
                </span>
                <h3 className="text-sm font-semibold text-fg">{f.title}</h3>
              </div>
              <p className="text-[13px] leading-relaxed text-fg-muted">{f.description}</p>
            </article>
          ))}
        </div>

        {/* Footer hint */}
        <div className="mt-8 rounded-lg border border-border/60 bg-bg-muted p-4 text-[13px] text-fg-muted">
          <p>
            <strong className="text-fg">Status:</strong> backend de tasks e dispatcher ainda não
            existe. TD27-09 (Lasso/Polygon) já entregou a seleção visual; falta o "verbo" (ação).
            Primeira iteração provavelmente sai como bulk-action via webhook (já wirado) + scheduler
            simples.
          </p>
          <p className="mt-2">
            Roadmap detalhado:{' '}
            <code className="rounded bg-bg-inset px-1 py-0.5">~/Documents/dev/TASKS.md</code> §
            techdash § Fase 3+ (TD27-09.1 Voalle Passa API real, TD27-09.2 canais notif).
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------- ícones inline ----------

function IconTasks() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-5 w-5"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <rect x="3" y="3" width="14" height="14" rx="2" />
      <path d="M6 10l3 3 5-6" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}

function IconBatch() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <rect x="3" y="3" width="6" height="6" rx="1" />
      <rect x="11" y="3" width="6" height="6" rx="1" />
      <rect x="3" y="11" width="6" height="6" rx="1" />
      <rect x="11" y="11" width="6" height="6" rx="1" />
    </svg>
  );
}

function IconCalendar() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <rect x="3" y="5" width="14" height="12" rx="2" />
      <path d="M3 8h14M7 3v3M13 3v3" strokeLinecap="round" />
    </svg>
  );
}

function IconRoute() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <circle cx="5" cy="5" r="2" />
      <circle cx="15" cy="15" r="2" />
      <path d="M5 7v4a3 3 0 003 3h4a3 3 0 013 3" strokeLinecap="round" />
    </svg>
  );
}

function IconHistory() {
  return (
    <svg
      viewBox="0 0 20 20"
      className="h-4 w-4"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      aria-hidden
    >
      <circle cx="10" cy="10" r="7" />
      <path d="M10 6v4l2.5 2.5" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
